import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../constants';

const ReportBugModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        type: 'Bug Visual', // This internal value might need to be kept in English or mapped if backend expects it. Assuming frontend display is what matters for now, or backend handles strings. If backend expects specific strings, we should map display strings to backend values. For now, let's assume we send English values or these values are display only. Actually, these are sent to backend. Let's keep internal values as keys or English if backend expects English, but display translated.
        // Strategy: Use a constant for options with value/label.
        subject: '',
        message: '',
        // Honeypot fields (initial empty)
        firstname: '',
        lastname: '',
        nickname: '',
        address: ''
    });
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState('idle'); // idle, sending, success, error
    const [errorMessage, setErrorMessage] = useState('');
    const fileInputRef = useRef(null);

    const reportTypes = [
        { value: 'Bug Visual', label: t('ReportBugModal.type_bug_visual') },
        { value: 'Error de Datos', label: t('ReportBugModal.type_data_error') },
        { value: 'Problema con mi Cuenta', label: t('ReportBugModal.type_account_issue') },
        { value: 'Sugerencia/Feedback', label: t('ReportBugModal.type_feedback') },
        { value: 'Otros', label: t('ReportBugModal.type_other') }
    ];

    if (!isOpen) return null;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        // Validation
        const validExts = ['.jpg', '.jpeg', '.png', '.webp'];
        const fileExt = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

        if (!validExts.includes(fileExt)) {
            setErrorMessage(t('ReportBugModal.error_format'));
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        if (selectedFile.size > 2 * 1024 * 1024) { // 2MB
            setErrorMessage(t('ReportBugModal.error_size'));
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setErrorMessage('');
        setFile(selectedFile);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');
        setErrorMessage('');

        try {
            const data = new FormData();
            // Append visible fields
            data.append('type', formData.type);
            data.append('subject', formData.subject);
            data.append('message', formData.message);

            // Append honeypot fields
            data.append('firstname', formData.firstname);
            data.append('lastname', formData.lastname);
            data.append('nickname', formData.nickname);
            data.append('address', formData.address);

            // Append file
            if (file) {
                data.append('screenshot', file);
            }

            // Interceptor handles Authorization
            // Note: Content-Type for FormData is automatically set by the browser,
            // explicitly setting 'application/json' would be incorrect here.
            const headers = {};

            const response = await fetch(`${API_BASE_URL}/api/feedback/report`, {
                method: 'POST',
                headers: headers,
                body: data // No Content-Type header needed for FormData
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || t('ReportBugModal.error_send'));
            }

            setStatus('success');

            // Auto close after 2 seconds
            setTimeout(() => {
                onClose();
                setStatus('idle');
                setFormData({
                    type: 'Bug Visual',
                    subject: '',
                    message: '',
                    firstname: '',
                    lastname: '',
                    nickname: '',
                    address: ''
                });
                setFile(null);
            }, 3000);

        } catch (error) {
            console.error(error);
            setStatus('error');
            setErrorMessage(error.message);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        {t('ReportBugModal.title')}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto">
                    {status === 'success' ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center animate-in zoom-in">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h4 className="text-xl font-bold text-gray-800 mb-2">{t('ReportBugModal.success_title')}</h4>
                            <p className="text-gray-500">{t('ReportBugModal.success_msg')}</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">

                            {/* Honeypot Fields (Hidden) */}
                            <div style={{ opacity: 0, position: 'absolute', top: 0, left: 0, height: 0, width: 0, zIndex: -1 }}>
                                <input type="text" name="firstname" value={formData.firstname} onChange={handleInputChange} tabIndex="-1" autoComplete="off" />
                                <input type="text" name="lastname" value={formData.lastname} onChange={handleInputChange} tabIndex="-1" autoComplete="off" />
                                <input type="text" name="nickname" value={formData.nickname} onChange={handleInputChange} tabIndex="-1" autoComplete="off" />
                                <input type="text" name="address" value={formData.address} onChange={handleInputChange} tabIndex="-1" autoComplete="off" />
                            </div>

                            {/* Visible Fields */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('ReportBugModal.label_type')}</label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                >
                                    {reportTypes.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('ReportBugModal.label_subject')}</label>
                                <input
                                    type="text"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder={t('ReportBugModal.placeholder_subject')}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('ReportBugModal.label_message')}</label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    rows="4"
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                    placeholder={t('ReportBugModal.placeholder_message')}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('ReportBugModal.label_screenshot')} <span className="text-gray-400 font-normal">{t('ReportBugModal.helper_screenshot')}</span>
                                </label>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept=".jpg,.jpeg,.png,.webp"
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                                />
                                {file && (
                                    <div className="mt-2 text-xs text-green-600 font-medium">
                                        {t('ReportBugModal.file_selected', { fileName: file.name })}
                                    </div>
                                )}
                            </div>

                            {errorMessage && (
                                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100">
                                    {errorMessage}
                                </div>
                            )}

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={status === 'sending'}
                                    className={`w-full py-2.5 rounded-lg font-bold text-white transition-all shadow-md
                                        ${status === 'sending'
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg active:scale-95'
                                        }
                                    `}
                                >
                                    {status === 'sending' ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('ReportBugModal.button_sending')}
                                        </span>
                                    ) : t('ReportBugModal.button_send')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReportBugModal;
// Revisado: 2025-12-18 [I18n Scan]
// Revisado: 2025-12-16
